// Thin client for the assistant-chat edge function. The function runs the
// Claude tool-use loop server-side and returns either (a) a text reply plus
// optional client-tool calls for the widget to execute (add_to_cart,
// request_details, open_checkout) or (b) an error.

import { supabase } from "../config";

export type AssistantClientToolCall =
  | {
      type: "add_to_cart";
      tool_use_id: string;
      input: {
        booking_id: string;
        service_id: string;
        service_name: string;
        service_type?: string;
        description: string;
        party_size?: number;
        amount_cents: number;
        currency: string;
        requires_full_payment?: boolean;
        starts_at?: string;
        ends_at?: string;
      };
    }
  | {
      type: "request_details";
      tool_use_id: string;
      input: { reason?: string };
    }
  | {
      type: "open_checkout";
      tool_use_id: string;
      input: { reason?: string };
    };

export type AssistantTurn =
  | {
      ok: true;
      reply: string;
      tool_calls: AssistantClientToolCall[];
      /**
       * Full content-block conversation (tool_use/tool_result included), as
       * the server ran it. Echo this back verbatim on the next turn so Claude
       * keeps its tool memory — availability it checked, holds it created.
       */
      history?: AssistantRequestMessage[];
      balance_cents: number;
    }
  | {
      ok: false;
      error:
        | "tier_not_eligible"
        | "no_credit"
        | "widget_not_found"
        | "upstream_error"
        | "network_error"
        | "server_not_configured"
        | "missing_fields"
        | string;
      balance_cents?: number;
    };

export interface AssistantRequestMessage {
  role: "user" | "assistant";
  content: unknown;
}

export async function invokeAssistant(input: {
  tenantSlug: string;
  widgetSlug: string;
  conversationId: string;
  cartToken: string;
  messages: AssistantRequestMessage[];
}): Promise<AssistantTurn> {
  try {
    const { data, error } = await supabase.functions.invoke("assistant-chat", {
      body: {
        tenant_slug: input.tenantSlug,
        widget_slug: input.widgetSlug,
        conversation_id: input.conversationId,
        cart_token: input.cartToken,
        messages: input.messages,
      },
    });
    if (error) {
      return { ok: false, error: error.message || "network_error" };
    }
    if (!data?.ok) {
      return { ok: false, error: data?.error ?? "network_error", balance_cents: data?.balance_cents };
    }
    return {
      ok: true,
      reply: data.reply ?? "",
      tool_calls: Array.isArray(data.tool_calls) ? data.tool_calls : [],
      history: Array.isArray(data.history) ? data.history : undefined,
      balance_cents: data.balance_cents ?? 0,
    };
  } catch (e) {
    return { ok: false, error: (e as Error).message || "network_error" };
  }
}
